import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { effects } from "../../data/effects";
import { Effect } from "../../types/effects";
import dreamWorldProvider from "../../DreamWorldProvider";
import { useSelector } from "react-redux";
import { selectUserGlobalData } from "../../store/dreamworldSlice";

interface EffectsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEffect: (effect: Effect) => void;
}

const EffectsPanel: React.FC<EffectsPanelProps> = ({
  isOpen,
  onClose,
  onSelectEffect,
}) => {
  const [userEffects, setUserEffects] = useState<Effect[]>([]);
  const userGlobalData = useSelector(selectUserGlobalData);

  useEffect(() => {
    if (isOpen && userGlobalData) {
      dreamWorldProvider
        .getUserCreatedEffects(userGlobalData.userData.uid)
        .then(setUserEffects);
    }
  }, [isOpen, userGlobalData]);

  const handleSelectEffect = (effect: Effect) => {
    onSelectEffect(effect);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="fixed bottom-0 left-0 right-0 h-2/3 bg-gray-900/95 backdrop-blur-xl border-t border-white/10 z-50 p-6 overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Choose an Effect</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <X size={20} className="text-white/60" />
        </button>
      </div>
      <div className="space-y-8">
        {userEffects.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-4">Your Effects</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {userEffects.map((effect) => (
                <motion.div
                  key={effect.id}
                  className="cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  onClick={() => handleSelectEffect(effect)}
                >
                  <img
                    src={effect.image}
                    alt={effect.title}
                    className="w-full h-32 object-cover rounded-lg mb-2"
                  />
                  <h3 className="text-white font-semibold">{effect.title}</h3>
                  <p className="text-white/70 text-sm">{effect.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        <div>
          <h3 className="text-lg font-bold text-white mb-4">DreamWorld Effects</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {effects.map((effect) => (
              <motion.div
                key={effect.id}
                className="cursor-pointer"
                whileHover={{ scale: 1.05 }}
                onClick={() => handleSelectEffect(effect)}
              >
                <img
                  src={effect.image}
                  alt={effect.title}
                  className="w-full h-32 object-cover rounded-lg mb-2"
                />
                <h3 className="text-white font-semibold">{effect.title}</h3>
                <p className="text-white/70 text-sm">{effect.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EffectsPanel;
